import { useMemo } from "react";
import { useSelector } from "react-redux";
import type { RoomProduct } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { HotelsShopSelectors } from "../../store";
import type { HotelRoomCardRatesProps } from "./HotelRoomCardRates";

export type UseHotelRoomCardRatesReturn = {
  context: {
    products: RoomProduct[];
    selectedProduct: RoomProduct;
  };
  handlers: {
    setSelectedProductWithRateId: (rateId: string) => void;
  };
  text: {
    selectedPrice: string;
    totalPrice: string;
  };
};

/**
 * Hook for HotelRoomCardRates - uses legacy products-based API.
 */
export const useHotelRoomCardRates = ({
  products,
  setSelectedProduct,
  selectedProduct,
  nightCount,
}: HotelRoomCardRatesProps): UseHotelRoomCardRatesReturn => {
  const { t, formatFiatCurrency } = useI18nContext();
  const roomCount = useSelector(HotelsShopSelectors.getRoomsCount);
  const nightlyPrice = formatFiatCurrency(selectedProduct.perNightTotal.fiat);
  const totalPrice = formatFiatCurrency(selectedProduct.tripTotal.fiat);

  return useMemo(
    () => ({
      context: {
        products,
        selectedProduct,
      },
      handlers: {
        setSelectedProductWithRateId: (rateId: string) => {
          const product = products.find((p) => p.rateId?.value === rateId);
          if (product != null) setSelectedProduct(product);
        },
      },
      text: {
        selectedPrice: t("core-hotels.shop.price.pricePerNight", {
          price: nightlyPrice,
        }),
        totalPrice:
          (roomCount ?? 0) > 1
            ? t(
                "core-hotels.shop.price.priceForNightsAndRoomsWithTaxesAndFees",
                {
                  price: totalPrice,
                  count: nightCount,
                  roomCount: roomCount,
                }
              )
            : t("core-hotels.shop.price.priceForNightsWithTaxesAndFees", {
                price: totalPrice,
                count: nightCount,
              }),
      },
    }),
    [
      nightCount,
      nightlyPrice,
      products,
      roomCount,
      selectedProduct,
      setSelectedProduct,
      t,
      totalPrice,
    ]
  );
};
